import type { ResearchRun, AnswerLength } from "../shared/research";
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let receiptKey:string|null=null;
  if(init?.method==='POST'&&typeof init.body==='string'&&(/^\/api\/deep-studies$/.test(path)||/^\/api\/sermons\/[^/]+\/(?:assistant|ai-drafts)$/.test(path))){
    const body=JSON.parse(init.body);
    if(!body.requestId){
      const bytes=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(path+init.body));
      receiptKey='sola-request:'+Array.from(new Uint8Array(bytes),value=>value.toString(16).padStart(2,'0')).join('');
      const requestId=sessionStorage.getItem(receiptKey)??crypto.randomUUID();
      sessionStorage.setItem(receiptKey,requestId);init={...init,body:JSON.stringify({...body,requestId})};
    }
  }
  const response = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "content-type": "application/json",
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const details = await response.json().catch(() => undefined);
    const message =
      details && typeof details === "object" && "error" in details
        ? String(details.error)
        : `Request failed (${response.status})`;
    if(receiptKey&&response.status<500)sessionStorage.removeItem(receiptKey);
    throw new ApiError(message, response.status, details);
  }

  if(receiptKey)sessionStorage.removeItem(receiptKey);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface StreamEvent {
  type: "meta" | "delta" | "done" | "error" | "research";
  runId?: string;
  progressLabel?: string;
  researchRun?: ResearchRun;
  conversationId?: string;
  messageId?: string;
  content?: string;
  error?: string;
}

export async function streamChat(
  body: {
    requestId?:string;
    answerLength?: AnswerLength;
    prompt: string;
    conversationId?: string;
    organizationId?: string;
    mode?: string;
    noteIds?: string[];
    searchNotes?: boolean;
    scriptureSelections?: ScriptureSelection[];
  },
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const digest=await crypto.subtle.digest('SHA-256',new TextEncoder().encode(JSON.stringify(body)));
  const signature=Array.from(new Uint8Array(digest),byte=>byte.toString(16).padStart(2,'0')).join('');
  let previous:{requestId:string;signature:string}|null=null;
  try{previous=JSON.parse(sessionStorage.getItem('sola-pending-request')??'null');}catch{/* Ignore an obsolete browser receipt. */}
  const requestId=body.requestId??(previous?.signature===signature?previous.requestId:crypto.randomUUID());
  sessionStorage.setItem('sola-pending-request',JSON.stringify({requestId,signature}));
  const response=await api<{runId:string;conversationId:string;messageId:string}>('/api/chat',{
    method:'POST',body:JSON.stringify({...body,requestId}),...(signal?{signal}:{}),
  });
  sessionStorage.setItem('sola-active-research',JSON.stringify(response));
  sessionStorage.removeItem('sola-pending-request');
  onEvent({type:'meta',...response});
  await resumeResearch(response.runId,onEvent,signal);
}

export async function resumeResearch(runId:string,onEvent:(event:StreamEvent)=>void,signal?:AbortSignal){
  let failures=0;
  while(true){
    signal?.throwIfAborted();
    let run:ResearchRun & {messageId:string;conversationId:string};
    try{run=await api<ResearchRun & {messageId:string;conversationId:string}>(`/api/research/runs/${runId}`,signal?{signal}:undefined);failures=0;}
    catch(error){
      if(signal?.aborted)throw error;
      if(++failures>5)throw new Error('Connection lost. Reload to resume the same research run.',{cause:error});
      await new Promise(resolve=>setTimeout(resolve,2000));continue;
    }
    onEvent({type:'research',runId,progressLabel:run.progressLabel});
    if(run.status==='complete'){
      onEvent({type:'research',runId,researchRun:run});
      onEvent({type:'delta',content:run.answer??''});
      onEvent({type:'done',conversationId:run.conversationId,messageId:run.messageId});
      sessionStorage.removeItem('sola-active-research');return;
    }
    if(run.status==='failed'||run.status==='canceled'){
      sessionStorage.removeItem('sola-active-research');throw new Error(run.error??'Research was canceled.');
    }
    await new Promise(resolve=>setTimeout(resolve,1500));
  }
}
import type { ScriptureSelection } from "../shared/bible";
